import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Bell, Loader2, User, Store, Shield } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { pushAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { hapticLight } from '@/lib/haptics';

// Group notification types by audience so the settings screen mirrors how the
// user thinks about them ("as a client I want to know…" vs "as a business…").
const GROUPS = [
  {
    id: 'client',
    icon: User,
    title: { es: 'Como cliente', en: 'As a client' },
    keys: [
      'booking_confirmed',
      'booking_reminder_24h',
      'booking_reminder_1h',
      'hold_expiring',
      'booking_canceled_by_business',
      'booking_rescheduled_by_business',
      'refund_processed',
      'review_request',
    ],
  },
  {
    id: 'business',
    icon: Store,
    title: { es: 'Como negocio', en: 'As a business' },
    keys: [
      'new_booking',
      'booking_canceled_by_customer',
      'new_review',
      'settlement_sent',
      'business_approved',
      'subscription_expired',
      'subscription_payment_failed',
      'trial_ending',
    ],
  },
  {
    id: 'admin',
    icon: Shield,
    title: { es: 'Como admin', en: 'As admin' },
    keys: ['business_pending_approval'],
    adminOnly: true,
  },
];

export default function NotificationPreferencesPage() {
  const { language } = useI18n();
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await pushAPI.getPreferences();
        setPrefs(res.data?.preferences || {});
        setLabels(res.data?.labels || {});
      } catch {
        toast.error(language === 'es' ? 'No se pudieron cargar tus preferencias' : 'Could not load preferences');
      } finally {
        setLoading(false);
      }
    })();
  }, [language]);

  const togglePref = async (key) => {
    const nextValue = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: nextValue }));
    setSaving((s) => ({ ...s, [key]: true }));
    hapticLight();
    try {
      await pushAPI.updatePreferences({ [key]: nextValue });
    } catch {
      // Rollback on failure
      setPrefs((p) => ({ ...p, [key]: !nextValue }));
      toast.error(language === 'es' ? 'No se pudo guardar' : 'Could not save');
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const t = (es, en) => (language === 'es' ? es : en);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-background" data-testid="notification-preferences-page">
      <div className="container-app py-6 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="prefs-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl leading-tight flex items-center gap-2">
              <Bell className="h-6 w-6 text-[#F05D5E]" />
              {t('Notificaciones', 'Notifications')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t(
                'Elige qué avisos quieres recibir. Puedes cambiarlos cuando quieras.',
                'Choose which notifications you want to receive. Change them anytime.'
              )}
            </p>
          </div>
        </div>

        {/* Groups */}
        <div className="space-y-4">
          {GROUPS.filter((g) => !g.adminOnly || isAdmin).map((group) => {
            const GroupIcon = group.icon;
            return (
              <Card key={group.id} className="border-border/60" data-testid={`prefs-group-${group.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 font-heading">
                    <GroupIcon className="h-4 w-4 text-muted-foreground" />
                    {t(group.title.es, group.title.en)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  {group.keys.map((key) => {
                    // Skip keys that the backend didn't label (unlikely, but future-proof)
                    if (!labels[key]) return null;
                    const isOn = prefs[key] !== false; // default ON
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between gap-3 py-1.5"
                        data-testid={`prefs-row-${key}`}
                      >
                        <label
                          htmlFor={`switch-${key}`}
                          className="text-sm flex-1 min-w-0 cursor-pointer select-none"
                        >
                          {labels[key]}
                        </label>
                        <Switch
                          id={`switch-${key}`}
                          checked={isOn}
                          onCheckedChange={() => togglePref(key)}
                          disabled={!!saving[key]}
                          data-testid={`prefs-switch-${key}`}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-6 text-center">
          {t(
            'Los cambios se guardan automáticamente.',
            'Changes save automatically.'
          )}
        </p>
      </div>
    </div>
  );
}
